import { getCurrentIdToken, initiateOAuthLogin, memoizedRefreshOAuthToken } from '@/services/auth/oauthService';
import { GraphQLClient, RequestDocument, Variables } from 'graphql-request';

export const graphQLClient = new GraphQLClient(import.meta.env.VITE_GRAPHQL_API_URL, {});

// Update client headers with Bearer token
function updateClientHeaders() {
  const idToken = getCurrentIdToken();
  if (idToken) {
    graphQLClient.setHeaders({
      Authorization: `Bearer ${idToken}`,
    });
  }
}

async function handleTokenRefresh(): Promise<boolean> {
  const success = await memoizedRefreshOAuthToken();
  if (success) {
    updateClientHeaders();
  }
  return success;
}

function isAuthError(error: any): boolean {
  const hasAuthError =
    error?.response?.errors?.some(
      (err: any) =>
        ['invalid-headers', 'invalid-jwt'].includes(err?.extensions?.code) ||
        ['Auth token is not valid!', 'You do not have permission to perform this action'].includes(err?.message) ||
        err?.message.includes('mutation_root') ||
        err?.message.includes('query_root'),
    ) || error?.response?.status === 401;
  if (hasAuthError) {
    console.log('DEBUG :: auth error:');
    console.log(error);
  }
  return hasAuthError;
}

async function retryRequest<T>(query: RequestDocument, variables?: Variables, noRedirect?: boolean): Promise<T> {
  const success = await handleTokenRefresh();
  if (success) {
    try {
      return await graphQLClient.request<T>(query, variables);
    } catch (retryError: any) {
      if (isAuthError(retryError) && !noRedirect) {
        console.log('Token is still invalid after refresh, redirecting to sign-in page');
        initiateOAuthLogin();
      }
      throw retryError;
    }
  } else {
    if (!noRedirect) {
      console.log('Failed to refresh token, redirecting to sign-in page');
      initiateOAuthLogin();
    }
    throw new Error('Token refresh failed');
  }
}

export async function fetchData<T = any>(
  query: RequestDocument,
  variables?: Variables,
  options?: {
    public?: boolean;
    /**
     * Still refresh the token and retry on auth-looking errors, but never redirect to
     * sign-in from this request. For best-effort reads whose failure is handled by the
     * caller: isAuthError matches 'query_root' substrings, so a root field the Hasura role
     * doesn't expose would otherwise hard-redirect the user even though their session is
     * valid — while an actually-expired token still recovers via the refresh.
     */
    noAuthRedirect?: boolean;
  },
): Promise<T> {
  if (options?.public) {
    graphQLClient.setHeaders({});
  } else {
    updateClientHeaders();
  }

  try {
    return await graphQLClient.request<T>(query, variables);
  } catch (error: any) {
    console.log('DEBUG :: fetchData error:', error);
    if (!options?.public && isAuthError(error)) {
      console.log('Auth error detected, attempting to refresh token...');
      return retryRequest(query, variables, options?.noAuthRedirect);
    }
    throw error;
  }
}
